import {
    useCallback,
    useState,
} from 'react'

/* A hook to manage a single localStorage entry.
 */
function useLocalStorage<T>({
    key,
    value,
    defaultValue,
}:{
    key: string,
    value: string,
    defaultValue: T,
}) {
    const [
        storedValue,
        setStoredValue,
    ] = useState<T>(() => {
        if (typeof window === 'undefined') return defaultValue
        try {
            const allTables = localStorage.getItem(key)

            if (!allTables) return defaultValue

            const allValues = JSON.parse(allTables) as Record<string, T>

            return allValues[value] ?? defaultValue
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error reading localStorage key “${key}”:`, error)
            return defaultValue
        }
    })

    const setSingleValue = (newValue: T) => {
        try {
            const allTables = localStorage.getItem(key)
            let allValues: Record<string, T> = {}

            if (allTables) {
                allValues = JSON.parse(allTables) as Record<string, T>
            }

            allValues[value] = {
                ...allValues[value] ?? {},
                ...newValue,
            }
            localStorage.setItem(key, JSON.stringify(allValues))
            setStoredValue(newValue)
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error setting localStorage key “${key}”:`, error)
        }
    }

    const getStorageValue = useCallback(() => {
        if (typeof window === 'undefined') return defaultValue
        try {
            const allTables = localStorage.getItem(key)

            if (!allTables) return defaultValue

            const allValues = JSON.parse(allTables) as Record<string, T>

            return allValues[value] ?? defaultValue
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error reading localStorage key “${key}”:`, error)
            return defaultValue
        }
    }, [
        defaultValue,
        value,
        key,
    ])

    return {
        dataGridState: storedValue,
        setDataGridState: setSingleValue,
        getStorageValue,
    }
}

export default useLocalStorage
